import { useMemo, useState } from 'react';
import {
  useWelcomeHomeEffectivenessQuery,
  useWelcomeHomeHygieneQuery,
} from '../api/queries';
import type {
  WhEffectivenessCommunity,
  WhEffectivenessRow,
  WhHygieneCommunity,
} from '../types';

type Props = { af: string | null };

export default function WelcomeHomeTab({ af }: Props) {
  const eff = useWelcomeHomeEffectivenessQuery(af);
  const hyg = useWelcomeHomeHygieneQuery(af);

  const communities = eff.data?.communities ?? [];
  const [activeId, setActiveId] = useState<number | null>(null);

  // Default the active sub-tab to the first community returned. Reset
  // whenever the franchise changes.
  const effectiveActive = useMemo(() => {
    if (activeId !== null && communities.some((c) => c.community_id === activeId)) {
      return activeId;
    }
    return communities[0]?.community_id ?? null;
  }, [activeId, communities]);

  if (!af) return <Placeholder />;

  if (eff.isLoading || hyg.isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: '#8a95a5' }}>
        <div className="spinner" style={{ margin: '0 auto 16px' }} />
        Loading WelcomeHome data...
      </div>
    );
  }

  if (eff.error || hyg.error) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: '#c0392b' }}>
        Error loading WelcomeHome data.
      </div>
    );
  }

  if (communities.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: '#8a95a5' }}>
        <Icon />
        <h3 style={{ margin: '0 0 8px' }}>No WelcomeHome data</h3>
        <p>No WelcomeHome account is linked to {af}.</p>
      </div>
    );
  }

  const active = communities.find((c) => c.community_id === effectiveActive) ?? communities[0];
  const hygActive = hyg.data?.communities.find((c) => c.community_id === active.community_id) ?? null;
  const network = hyg.data?.network ?? null;
  const showSubTabs = communities.length > 1;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '12px 16px' }}>
      <div style={{ marginBottom: 8 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 20, color: '#1a2a3a' }}>
          WelcomeHome — Marketing Effectiveness
        </h2>
        <div style={{ fontSize: 13, color: '#8a95a5' }}>
          Trailing 90 days. {eff.data?.synced_at && `Last synced: ${formatDate(eff.data.synced_at)}.`}
        </div>
      </div>

      {showSubTabs && (
        <div
          style={{
            display: 'flex',
            gap: 4,
            margin: '16px 0 4px',
            borderBottom: '1px solid #e5eaf1',
          }}
        >
          {communities.map((c) => {
            const isActive = c.community_id === active.community_id;
            return (
              <button
                key={c.community_id}
                onClick={() => setActiveId(c.community_id)}
                style={{
                  padding: '10px 16px',
                  fontSize: 14,
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? '#2c5da8' : '#5a6573',
                  background: isActive ? '#fff' : 'transparent',
                  border: 'none',
                  borderBottom: isActive ? '2px solid #2c5da8' : '2px solid transparent',
                  cursor: 'pointer',
                  marginBottom: -1,
                }}
              >
                {c.af_number}
              </button>
            );
          })}
        </div>
      )}

      <EffectivenessMatrix community={active} />

      <HygienePanel community={hygActive} network={network} />
    </div>
  );
}

// ── Effectiveness matrix ──────────────────────────────────────────

function EffectivenessMatrix({ community }: { community: WhEffectivenessCommunity }) {
  const rows = community.rows;

  const [sortKey, setSortKey] = useState<keyof WhEffectivenessRow>('leads');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [showLowVolume, setShowLowVolume] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const INITIAL_ROWS = 10;

  const sorted = useMemo(() => {
    const r = [...rows];
    r.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      const av = va === null ? -1 : Number(va);
      const bv = vb === null ? -1 : Number(vb);
      if (av === bv) return a.leadSourceName.localeCompare(b.leadSourceName);
      return sortDir === 'desc' ? bv - av : av - bv;
    });
    return r;
  }, [rows, sortKey, sortDir]);

  const visible = showLowVolume ? sorted : sorted.filter((r) => r.leads >= 5);
  const hiddenCount = Math.max(0, visible.length - INITIAL_ROWS);
  const displayed = expanded ? visible : visible.slice(0, INITIAL_ROWS);

  const totals = sorted.reduce(
    (acc, r) => {
      acc.leads += r.leads;
      acc.reachedAssessment += r.reachedAssessment;
      acc.reachedIntake += r.reachedIntake;
      acc.reachedStartedService += r.reachedStartedService;
      acc.closedLost += r.closedLost;
      return acc;
    },
    { leads: 0, reachedAssessment: 0, reachedIntake: 0, reachedStartedService: 0, closedLost: 0 },
  );

  function handleSort(k: keyof WhEffectivenessRow) {
    if (k === sortKey) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(k); setSortDir('desc'); }
  }

  function pct(num: number, denom: number, isLowVolume: boolean): string {
    if (!denom) return '—';
    const p = (num / denom) * 100;
    return `${num} (${isLowVolume ? '*' : ''}${p.toFixed(0)}%)`;
  }

  // Color thresholds: ±10pp from the row's own franchise median (overall conversion).
  const franchiseStartedRate =
    totals.leads > 0 ? totals.reachedStartedService / totals.leads : 0;
  function rateColor(num: number, denom: number, isLowVolume: boolean): string {
    if (!denom || isLowVolume) return '#5a6573';
    const r = num / denom;
    if (r >= franchiseStartedRate + 0.1) return '#27ae60';
    if (r <= franchiseStartedRate - 0.1) return '#c0392b';
    return '#5a6573';
  }

  if (rows.length === 0) {
    return (
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 32,
          marginTop: 16,
          textAlign: 'center',
          color: '#8a95a5',
        }}
      >
        No leads in the last 90 days for {community.af_number}.
      </div>
    );
  }

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
        marginTop: 16,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '14px 20px',
          borderBottom: '1px solid #eef1f5',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1a2a3a' }}>
          Lead Source Effectiveness — {community.af_number}
        </h3>
        <label style={{ fontSize: 12, color: '#5a6573', display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={showLowVolume}
            onChange={(e) => setShowLowVolume(e.target.checked)}
          />
          Show sources with &lt;5 leads (rates marked with *)
        </label>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f6f8fb', textAlign: 'left' }}>
              <Th onClick={() => handleSort('leadSourceName')} active={sortKey === 'leadSourceName'} dir={sortDir}>
                Lead Source
              </Th>
              <Th onClick={() => handleSort('leadSourceCategory')} active={sortKey === 'leadSourceCategory'} dir={sortDir}>
                Category
              </Th>
              <Th onClick={() => handleSort('leads')} active={sortKey === 'leads'} dir={sortDir} align="right">
                Leads
              </Th>
              <Th onClick={() => handleSort('reachedAssessment')} active={sortKey === 'reachedAssessment'} dir={sortDir} align="right">
                → Assessment
              </Th>
              <Th onClick={() => handleSort('reachedIntake')} active={sortKey === 'reachedIntake'} dir={sortDir} align="right">
                → Intake
              </Th>
              <Th onClick={() => handleSort('reachedStartedService')} active={sortKey === 'reachedStartedService'} dir={sortDir} align="right">
                → Started Service
              </Th>
              <Th onClick={() => handleSort('closedLost')} active={sortKey === 'closedLost'} dir={sortDir} align="right">
                Lost / Closed
              </Th>
              <Th onClick={() => handleSort('avgDaysToCare')} active={sortKey === 'avgDaysToCare'} dir={sortDir} align="right">
                Avg Days to Care
              </Th>
            </tr>
          </thead>
          <tbody>
            {displayed.map((r, i) => {
              const lowVol = r.leads < 5;
              return (
                <tr
                  key={`${r.leadSourceId ?? 'null'}-${r.leadSourceName}`}
                  style={{
                    background: i % 2 ? '#fafbfd' : '#fff',
                    color: lowVol ? '#9aa3b0' : '#1a2a3a',
                  }}
                >
                  <Td>{r.leadSourceName}</Td>
                  <Td>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 10,
                        fontSize: 11,
                        background: categoryBg(r.leadSourceCategory),
                        color: '#3a4a5a',
                      }}
                    >
                      {r.leadSourceCategory}
                    </span>
                  </Td>
                  <Td align="right">{r.leads}</Td>
                  <Td align="right" color={rateColor(r.reachedAssessment, r.leads, lowVol)}>
                    {pct(r.reachedAssessment, r.leads, lowVol)}
                  </Td>
                  <Td align="right" color={rateColor(r.reachedIntake, r.leads, lowVol)}>
                    {pct(r.reachedIntake, r.leads, lowVol)}
                  </Td>
                  <Td align="right" color={rateColor(r.reachedStartedService, r.leads, lowVol)}>
                    {pct(r.reachedStartedService, r.leads, lowVol)}
                  </Td>
                  <Td align="right">{pct(r.closedLost, r.leads, lowVol)}</Td>
                  <Td align="right">{r.avgDaysToCare !== null ? r.avgDaysToCare.toFixed(1) : '—'}</Td>
                </tr>
              );
            })}
            {hiddenCount > 0 && (
              <tr>
                <td colSpan={8} style={{ padding: 0, borderTop: '1px solid #eef1f5' }}>
                  <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    style={{
                      width: '100%',
                      background: '#fafbfd',
                      border: 'none',
                      padding: '10px 12px',
                      cursor: 'pointer',
                      fontSize: 12,
                      color: '#3a4a5a',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {expanded ? (
                      <>Show less <span style={{ fontSize: 10 }}>▲</span></>
                    ) : (
                      <>Show {hiddenCount} more source{hiddenCount === 1 ? '' : 's'} <span style={{ fontSize: 10 }}>▼</span></>
                    )}
                  </button>
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr style={{ background: '#f6f8fb', fontWeight: 600 }}>
              <Td>Total</Td>
              <Td>—</Td>
              <Td align="right">{totals.leads}</Td>
              <Td align="right">{pct(totals.reachedAssessment, totals.leads, false)}</Td>
              <Td align="right">{pct(totals.reachedIntake, totals.leads, false)}</Td>
              <Td align="right">{pct(totals.reachedStartedService, totals.leads, false)}</Td>
              <Td align="right">{pct(totals.closedLost, totals.leads, false)}</Td>
              <Td align="right">—</Td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function categoryBg(category: string): string {
  switch (category) {
    case 'Aggregator': return '#fef3e0';
    case 'Online': return '#e1efff';
    case 'Professional Referral': return '#e6f5e6';
    case 'Trusted Advisors': return '#f0e8f7';
    case 'Non-Professional Referral': return '#fde6e6';
    case 'Advertising':
    case 'Advertisement': return '#fff5d6';
    default: return '#eef1f5';
  }
}

// ── Hygiene panel ──────────────────────────────────────────────────

function HygienePanel({
  community,
  network,
}: {
  community: WhHygieneCommunity | null;
  network: { medianSpeedToLeadBhMinutes: number | null; medianSpeedToLeadRawMinutes: number | null; avgActivitiesThroughAssessment: number | null } | null;
}) {
  if (!community || !network) return null;

  const cards = [
    {
      label: 'Median speed-to-lead (business hours)',
      value: community.medianSpeedToLeadBhMinutes,
      networkValue: network.medianSpeedToLeadBhMinutes,
      unit: 'min',
      lowerIsBetter: true,
    },
    {
      label: 'Median speed-to-lead (raw clock)',
      value: community.medianSpeedToLeadRawMinutes,
      networkValue: network.medianSpeedToLeadRawMinutes,
      unit: 'min',
      lowerIsBetter: true,
    },
    {
      label: 'Avg activities per prospect through Assessment',
      value: community.avgActivitiesThroughAssessment,
      networkValue: network.avgActivitiesThroughAssessment,
      unit: '',
      lowerIsBetter: false,
    },
  ];

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
        marginTop: 16,
        padding: 20,
      }}
    >
      <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600, color: '#1a2a3a' }}>
        Sales Hygiene — {community.af_number}
      </h3>
      <div style={{ fontSize: 12, color: '#8a95a5', marginBottom: 16 }}>
        Process metrics this franchise controls (last 90d). Compared to network median.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        {cards.map((c) => (
          <HygieneCard key={c.label} {...c} />
        ))}
      </div>
      <div style={{ fontSize: 11, color: '#9aa3b0', marginTop: 12 }}>
        Sample size for speed-to-lead: {community.sampleSize} prospects.
      </div>
    </div>
  );
}

function HygieneCard({
  label,
  value,
  networkValue,
  unit,
  lowerIsBetter,
}: {
  label: string;
  value: number | null;
  networkValue: number | null;
  unit: string;
  lowerIsBetter: boolean;
}) {
  const fmt = (n: number | null) => (n === null ? '—' : `${n.toFixed(unit === 'min' ? 0 : 1)}${unit ? ' ' + unit : ''}`);
  let color = '#5a6573';
  if (value !== null && networkValue !== null && networkValue > 0) {
    const ratio = value / networkValue;
    const better = lowerIsBetter ? ratio <= 0.85 : ratio >= 1.15;
    const worse = lowerIsBetter ? ratio >= 1.15 : ratio <= 0.85;
    if (better) color = '#27ae60';
    if (worse) color = '#c0392b';
  }
  return (
    <div style={{ padding: 16, background: '#f6f8fb', borderRadius: 8 }}>
      <div style={{ fontSize: 12, color: '#5a6573', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{fmt(value)}</div>
      <div style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
        Network median: {fmt(networkValue)}
      </div>
    </div>
  );
}

// ── small bits ─────────────────────────────────────────────────────

function Th({
  children,
  onClick,
  active,
  dir,
  align = 'left',
}: {
  children: React.ReactNode;
  onClick: () => void;
  active: boolean;
  dir: 'asc' | 'desc';
  align?: 'left' | 'right';
}) {
  return (
    <th
      onClick={onClick}
      style={{
        padding: '10px 12px',
        textAlign: align,
        cursor: 'pointer',
        borderBottom: '1px solid #e5eaf1',
        fontSize: 12,
        fontWeight: 600,
        color: active ? '#2c5da8' : '#3a4a5a',
        whiteSpace: 'nowrap',
        userSelect: 'none',
      }}
    >
      {children}
      {active && <span style={{ marginLeft: 4, fontSize: 10 }}>{dir === 'desc' ? '▼' : '▲'}</span>}
    </th>
  );
}

function Td({ children, align = 'left', color }: { children: React.ReactNode; align?: 'left' | 'right'; color?: string }) {
  return (
    <td
      style={{
        padding: '8px 12px',
        textAlign: align,
        whiteSpace: 'nowrap',
        color,
      }}
    >
      {children}
    </td>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function Placeholder() {
  return (
    <div className="tab-placeholder">
      <Icon />
      <h3>WelcomeHome</h3>
      <p>Select a franchisee to view their WelcomeHome marketing effectiveness</p>
    </div>
  );
}

function Icon() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      style={{ opacity: 0.3, marginBottom: 12 }}
    >
      <path d="M3 11l9-8 9 8M5 10v10a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V10" />
    </svg>
  );
}
